import { DragDropContext,Draggable,Droppable,DropResult } from "@hello-pangea/dnd";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MenuIcon from '@mui/icons-material/Menu';
import { Button,Card,Checkbox,Divider,FormControl,FormControlLabel,IconButton,InputLabel,MenuItem,Select,SelectChangeEvent,Stack,TextField,Typography } from "@mui/material";
import Grid from '@mui/material/Grid';
import { useContext,useEffect,useState } from "react";
import { useTranslation } from "react-i18next";
import { NewFormContext } from "../../context/NewFormContext";
import { ChecklistType } from "../../types/checklist/types";
import EditItem from "./EditItem";

const tiposIdentificador = ['textoCurto', 'telefone', 'rg', 'cpfcnpj', 'cep', 'placa'];



export default function EditModel() {
  const { t } = useTranslation('translation');
  const { modelo, setModelo } = useContext(NewFormContext);
  const [dragDisable, setDragDisable] = useState(false);
  const [open, setOpen] = useState<string[]>([]);

  const handelOpen = (value: string) => {
    if (open.includes(value)) {
      setOpen((prev) => prev.filter((ele) => ele !== value));
    } else {
      setOpen(prev => [...prev, value]);

    };
  };

  useEffect(() => {
    if (open?.length) {
      handleDragDisable(true);
    } else {
      handleDragDisable(false);
    }
  }, [open]);

  const [listaIdentificador, setListaIdentificador] = useState<{ value: string; nome: string; }[]>([]);

  useEffect(() => {
    if (!modelo?.itens) return;

    const novaLista: { value: string; nome: string; }[] = [];

    modelo.itens.forEach((item, itemIndex) => {
      item.subitens?.forEach((sub, subIndex) => {
        if (tiposIdentificador.includes(sub.tipo)) {
          novaLista.push({
            value: `${modelo.id}|${modelo.versao}|${itemIndex}|${subIndex}`,
            nome: `${itemIndex + 1}.${subIndex + 1} - ${sub.nome}`
          });
        }
      });
    });

    setListaIdentificador(novaLista);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelo?.itens, modelo?.id, modelo?.versao, tiposIdentificador]);

  function handleAddNewItem() {
    setModelo((prevModelo: ChecklistType) => ({
      ...prevModelo,
      itens: [
        ...prevModelo.itens,
        {
          id: `${prevModelo.id}|${prevModelo.versao + 1}|${prevModelo.itens.length}`,
          nome: 'Novo Item',
          peso: 1,
          subitens: [{
            id: 0,
            nome: 'Subitem',
            itemid: `${prevModelo.id}|${prevModelo.versao + 1}|${prevModelo.itens.length}`,
            tipo: 'avaliacao',
            avaliacao: [{ nome: '1', valor: 0 }, { nome: '2', valor: prevModelo.valorMaxScoring }],
            peso: 1,
            threshold: -1
          }]
        }
      ]
    }));
  }

  function handleInpuChange(key: string, value: string | number) {
    setModelo((prev: ChecklistType) => ({ ...prev, [key]: value }));
  }

  function handleChangeSelect(eve: SelectChangeEvent<string>) {
    setModelo((prev: ChecklistType) => ({ ...prev, identificadorLista: eve.target.value }));
  }

  function reorder(list: [], startIndex: number, endIndex: number) {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  function handleDragEnd(result: DropResult) {
    if (!result.destination) {
      return;
    }

    const items = reorder(
      modelo?.itens as [],
      result.source.index,
      result.destination.index
    );

    setModelo((prev: ChecklistType) => ({
      ...prev,
      itens: items
    }));
  };

  const handleDragDisable = (value: boolean) => {
    setDragDisable(value);
  };

  return (
    <Card sx={{ width: '100%', padding: 2 }}>
      <Stack direction='column' alignItems='center' justifyContent='flex-start' spacing={1.5} padding={1}>
        <Typography color='success' textAlign='center' fontWeight={700}
          sx={{ fontSize: 'clamp(1.75rem, 1.5357rem + 1.0714vw, 3.25rem);', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', textOverflow: 'ellipsis', overflow: 'hidden' }}>
          {modelo?.nome}
        </Typography>
        <Typography variant='body1' fontWeight={600}>
          {modelo?.preenchimentoDeslogado ? 'Web | Anônimo' : 'Logado | Hierárquico'}
        </Typography>
        <Grid container width='100%' padding={1}>
          <Stack direction='row' alignItems='center' width={'100%'} spacing={1}>
            <Grid size={8}>
              <TextField
                inputProps={{ maxLength: 200 }}
                fullWidth
                label={'Nome do modelo'}
                value={modelo?.nome ?? ''}
                onChange={eve => handleInpuChange('nome', eve.target.value)}
              />
            </Grid>
            <Grid size={1.5}>
              <TextField
                fullWidth
                inputProps={{
                  min: 0,
                  max: 100,
                  step: 1,
                }}
                type="number"
                label={'Valor max scoring'}
                value={modelo?.valorMaxScoring ?? 100}
                onChange={eve => handleInpuChange('valorMaxScoring', parseInt(eve.target.value))}
              />
            </Grid>
            <Grid size={2.5}>
              {!listaIdentificador.length
                ? <Card sx={{ backgroundColor: '#FFE08A', padding: 1 }}>
                  <Typography textAlign='center' fontSize='small'>
                    {t('checklists.noIdentifierOption')}
                  </Typography>
                </Card>
                : <FormControl fullWidth>
                  <InputLabel>{t('checklists.checklists.identifier')}</InputLabel>
                  <Select
                    value={modelo?.identificadorLista
                      ? modelo?.identificadorLista?.replace(
                        /^([^|]*)\|([^|]*)/,
                        (_, p1) => `${p1}|${modelo?.versao}`
                      )
                      : ""}
                    label={t('checklists.checklists.identifier')}
                    onChange={handleChangeSelect}
                  >
                    {listaIdentificador?.map((e, i) => (
                      <MenuItem value={e?.value} key={i}>{e?.nome}</MenuItem>))}
                  </Select>
                </FormControl>}
            </Grid>
          </Stack>
        </Grid>
        <Stack direction='row' alignItems='center' justifyContent='center' spacing={1}>
          {!modelo?.preenchimentoDeslogado && <>
            <FormControlLabel labelPlacement="start"
              control={<Checkbox checked={modelo?.editavel?.blockCancel ?? false}
                onChange={() => setModelo((prev: ChecklistType) => ({
                  ...prev,
                  editavel: {
                    ...prev?.editavel,
                    blockCancel: !prev?.editavel?.blockCancel
                  }
                }))}
              />} label='Bloquear cancelamento' />
            <FormControlLabel labelPlacement="start"
              control={<Checkbox checked={modelo?.editavel?.blockClone ?? false}
                onChange={() => setModelo((prev: ChecklistType) => ({
                  ...prev,
                  editavel: {
                    ...prev?.editavel,
                    blockClone: !prev?.editavel?.blockClone
                  }
                }))}
              />} label='Bloquear clonagem' />
          </>}
          <FormControlLabel labelPlacement="start"
            control={<Checkbox checked={modelo?.editavel?.mandatory ?? false}
              onChange={() => setModelo((prev: ChecklistType) => ({
                ...prev,
                editavel: {
                  ...prev?.editavel,
                  mandatory: !prev?.editavel?.mandatory
                }
              }))}
            />} label='Obrigar comentário na aprovação' />
        </Stack>
        <Typography variant="h4" fontWeight={600} color='textDisabled'>Itens e subitens</Typography>
        <Stack direction='row' alignItems='center' justifyContent='flex-end' width='100%'>
          <Button
            color="success"
            onClick={handleAddNewItem}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 800, fontSize: 18 }}
            size="small"
            endIcon={<AddCircleOutlineIcon />}
          >
            Novo item
          </Button>
        </Stack>
        <Divider variant="middle" color='success' flexItem orientation='horizontal' sx={{
          borderColor: '#00C4A7',
          borderWidth: 2,
        }} />
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="avaliacao">
            {(provided) => (
              <Stack width='100%' direction='column' spacing={.5} {...provided.droppableProps} ref={provided.innerRef}>
                {modelo?.itens?.map((item, index) => (
                  <Draggable
                    isDragDisabled={dragDisable || modelo?.itens?.length <= 1}
                    key={item.id}
                    draggableId={item.id}
                    index={index}
                  >
                    {(provided) => (
                      <Stack
                        alignItems='flex-start'
                        justifyContent='flex-start'
                        direction='row'
                        spacing={.5}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                      >
                        <div style={{ marginTop: 16 }} {...provided.dragHandleProps}>
                          <IconButton disabled={modelo?.itens?.length <= 1 || dragDisable}>
                            <MenuIcon />
                          </IconButton>
                        </div>
                        <EditItem
                          open={open}
                          handleOpenItem={handelOpen}
                          item={item}
                          index={index}
                        />
                      </Stack>
                    )}
                  </Draggable>
                ))}
              </Stack>)}
          </Droppable>
        </DragDropContext>
      </Stack>
    </Card>
  );
}
